const EXCLUDED_FIELDS = new Set([
  "member_since",
  "is_member",
  "subscription_status_updated_at",
]);

// type: text | number | decimal | flag | date | datetime
export const CUSTOMER_FORM_SECTIONS = [
  {
    key: "profile",
    title: "Profile",
    description: "Basic customer identity and demographics",
    fields: [
      { key: "age", label: "Age", type: "number", sample: "34" },
      { key: "date_of_birth", label: "Date of Birth", type: "date", sample: "1992-05-18" },
      { key: "newsletter_region", label: "Newsletter Region", type: "text", sample: "UK" },
      { key: "dob_is_today", label: "DOB Is Today", type: "flag", sample: "0 or 1" },
      { key: "dob_is_in_7_days", label: "DOB In 7 Days", type: "flag", sample: "0 or 1" },
    ],
  },
  {
    key: "accounts",
    title: "Accounts & Consent",
    description: "Store accounts and subscription preferences",
    fields: [
      { key: "account_uk", label: "Account UK", type: "flag", sample: "0 or 1" },
      { key: "account_us_ca", label: "Account US/CA", type: "flag", sample: "0 or 1" },
      { key: "account_de", label: "Account DE", type: "flag", sample: "0 or 1" },
      { key: "account_int", label: "Account INT", type: "flag", sample: "0 or 1" },
      { key: "account_uat", label: "Account UAT", type: "flag", sample: "0 or 1" },
      { key: "email_subscribe", label: "Email Subscribe", type: "flag", sample: "0 or 1" },
      { key: "clienteling_consent", label: "Clienteling Consent", type: "flag", sample: "0 or 1" },
      { key: "opted_timestamp", label: "Opted Timestamp", type: "datetime", sample: "2024-06-12 10:00:00" },
      { key: "clienteling_consent_timestamp", label: "Clienteling Consent Timestamp", type: "datetime", sample: "2024-06-12 10:05:00" },
    ],
  },
  {
    key: "spa",
    title: "SPA",
    description: "SPA customer activity and spending",
    fields: [
      { key: "is_spa_customer", label: "Is SPA Customer", type: "flag", sample: "0 or 1" },
      { key: "spa_first_order_date", label: "SPA First Order Date", type: "date", sample: "2023-01-15" },
      { key: "spa_last_12_months_orders", label: "Last 12 Months Orders", type: "number", sample: "4" },
      { key: "spa_last_12_months_spending", label: "Last 12 Months Spending", type: "decimal", sample: "1250.50" },
      { key: "spa_last_12_months_aov", label: "Last 12 Months AOV", type: "decimal", sample: "312.63" },
    ],
  },
  {
    key: "online",
    title: "Online / Website",
    description: "Website orders, spend, and engagement flags",
    fields: [
      { key: "is_website_customer", label: "Is Website Customer", type: "flag", sample: "0 or 1" },
      { key: "online_first_order_date", label: "First Order Date", type: "date", sample: "2022-08-01" },
      { key: "online_lifetime_orders", label: "Lifetime Orders", type: "number", sample: "12" },
      { key: "online_lifetime_spending", label: "Lifetime Spending", type: "decimal", sample: "3480.00" },
      { key: "online_lifetime_aov", label: "Lifetime AOV", type: "decimal", sample: "290.00" },
      { key: "online_last_order_date", label: "Last Order Date", type: "datetime", sample: "2026-03-01 14:30:00" },
      { key: "online_days_since_last_order", label: "Days Since Last Order", type: "number", sample: "45" },
      { key: "online_last_12_months_orders", label: "Last 12 Months Orders", type: "number", sample: "6" },
      { key: "online_last_12_months_spending", label: "Last 12 Months Spending", type: "decimal", sample: "980.25" },
      { key: "online_last_12_months_aov", label: "Last 12 Months AOV", type: "decimal", sample: "163.38" },
      { key: "online_is_anniversary", label: "Is Anniversary", type: "flag", sample: "0 or 1" },
      { key: "online_is_lapsed", label: "Is Lapsed", type: "flag", sample: "0 or 1" },
    ],
  },
  {
    key: "omnichannel",
    title: "Omnichannel",
    description: "Combined online and offline customer metrics",
    fields: [
      { key: "is_omnichannel_customer", label: "Is Omnichannel Customer", type: "flag", sample: "0 or 1" },
      { key: "omnichannel_first_order_date", label: "First Order Date", type: "date", sample: "2021-11-20" },
      { key: "omnichannel_lifetime_orders", label: "Lifetime Orders", type: "number", sample: "20" },
      { key: "omnichannel_lifetime_spending", label: "Lifetime Spending", type: "decimal", sample: "5200.75" },
      { key: "omnichannel_lifetime_aov", label: "Lifetime AOV", type: "decimal", sample: "260.04" },
      { key: "omnichannel_last_order_date", label: "Last Order Date", type: "datetime", sample: "2026-02-10 09:15:00" },
      { key: "omnichannel_days_since_last_order", label: "Days Since Last Order", type: "number", sample: "30" },
      { key: "omnichannel_last_12_months_orders", label: "Last 12 Months Orders", type: "number", sample: "8" },
      { key: "omnichannel_last_12_months_spending", label: "Last 12 Months Spending", type: "decimal", sample: "1450.00" },
      { key: "omnichannel_last_12_months_aov", label: "Last 12 Months AOV", type: "decimal", sample: "181.25" },
    ],
  },
  {
    key: "prive",
    title: "Privé Membership",
    description: "Tier, spend, and membership risk signals",
    fields: [
      { key: "prive_customer", label: "Privé Customer", type: "flag", sample: "0 or 1" },
      { key: "prive_tier", label: "Privé Tier", type: "text", sample: "Gold" },
      { key: "prive_membership_start_date", label: "Membership Start Date", type: "date", sample: "2024-01-01" },
      { key: "prive_membership_end_date", label: "Membership End Date", type: "date", sample: "2025-01-01" },
      { key: "prive_total_9month_spend_local_currency", label: "9-Month Spend (Local)", type: "decimal", sample: "2500.00" },
      { key: "prive_local_currency", label: "Local Currency", type: "text", sample: "GBP" },
      { key: "prive_membership_loss_at_risk", label: "Membership Loss At Risk", type: "flag", sample: "0 or 1" },
      { key: "prive_membership_tierloss_at_risk", label: "Tier Loss At Risk", type: "flag", sample: "0 or 1" },
      { key: "prive_removal", label: "Removal", type: "flag", sample: "0 or 1" },
      { key: "prive_upgrade", label: "Upgrade", type: "flag", sample: "0 or 1" },
      { key: "prive_downgrade", label: "Downgrade", type: "flag", sample: "0 or 1" },
    ],
  },
  {
    key: "rewards",
    title: "Rewards",
    description: "Welcome, anniversary, birthday, and lapsed rewards",
    fields: [
      { key: "welcome_redeemed_flag", label: "Welcome Redeemed", type: "flag", sample: "0 or 1" },
      { key: "anniversary_gwp_redeemed_date", label: "Anniversary GWP Redeemed Date", type: "date", sample: "2025-08-01" },
      { key: "birthday_reward_redeemed_date", label: "Birthday Reward Redeemed Date", type: "date", sample: "2025-05-18" },
      { key: "lapsed_reward_redeemed_date", label: "Lapsed Reward Redeemed Date", type: "date", sample: "2025-09-01" },
    ],
  },
].map((section) => ({
  ...section,
  fields: section.fields.filter((field) => !EXCLUDED_FIELDS.has(field.key)),
}));

export const CUSTOMER_FORM_FIELDS = CUSTOMER_FORM_SECTIONS.flatMap(
  (section) => section.fields
);

export default CUSTOMER_FORM_FIELDS;
